import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Modal, Button, IonIcon, Spinner } from 'controls'
import { CodeConductActionCreators } from 'app/reducers/CodeConductRedux'
import { ContractVersionActionCreators } from 'app/reducers/ContractVersionRedux'
import { urlForContractSelector } from 'app/selectors/contractVersion'
import PDFViewer from 'components/PDFViewer'
import { codeConductModalStyles, oldVersionCodeConductModalStyles } from './styles'

class CodeConductModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    checkConsentType: PropTypes.func.isRequired,
    acceptCodeConductContract: PropTypes.func.isRequired,
    getUrlByContract: PropTypes.func.isRequired,
    codeConductFeedback: PropTypes.object,
    consentType: PropTypes.string,
    documentUrl: PropTypes.string,
    isAccepting: PropTypes.bool,
    contractUrlData: PropTypes.object,
  }

  static defaultProps = {
    isOpen: false,
    codeConductFeedback: null,
    consentType: null,
    documentUrl: null,
    isAccepting: false,
    contractUrlData: null,
  }

  state = {
    hasScrollCompleted: false
  }

  componentDidMount() {
    // Check consent type when component mounts
    if (this.props.isOpen) {
      this.props.checkConsentType()
      // Also fetch the signing URL for old version workflow
      this.props.getUrlByContract({ contract_type: 'code-conduct' })
    }
  }

  componentDidUpdate(prevProps) {
    // Check consent type when modal opens
    if (!prevProps.isOpen && this.props.isOpen) {
      // Reset scroll completion state when modal opens
      this.setState({ hasScrollCompleted: false })
      this.props.checkConsentType()
      // Also fetch the signing URL for old version workflow
      this.props.getUrlByContract({ contract_type: 'code-conduct' })
    }

    // Handle successful acceptance - close modal
    if (
      prevProps.codeConductFeedback?.acceptanceLoading &&
      !this.props.codeConductFeedback?.acceptanceLoading &&
      !this.props.codeConductFeedback?.acceptanceError
    ) {
      // Success - close modal after a brief delay to show success state
      setTimeout(() => {
        this.props.onClose()
      }, 1000)
    }
  }

  handleAcceptContract = () => {
    this.props.acceptCodeConductContract()
  }

  handleScrollComplete = () => {
    this.setState({ hasScrollCompleted: true })
  }

  renderLoadingState = () => (
    <div className="code-conduct-modal-loading">
      <div className="loading-content">
        <Spinner size={30} />
        <h3>Cargando información del contrato...</h3>
        <p>Por favor, espere mientras verificamos su información.</p>
      </div>

      <style jsx>{codeConductModalStyles}</style>
    </div>
  )

  renderErrorState = () => {
    const { codeConductFeedback } = this.props
    const error = codeConductFeedback?.error || 'Error desconocido'

    return (
      <div className="code-conduct-modal-error">
        <div className="error-content">
          <IonIcon icon="ios-warning" />
          <h3>Error al cargar el contrato</h3>
          <p>{error}</p>
        </div>
        <div className="error-actions">
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => this.props.checkConsentType()}
          >
            <IonIcon icon="ios-refresh" />
            Reintentar
          </Button>
          <Button
            type="button"
            color="danger"
            outline
            onClick={this.props.onClose}
          >
            Cerrar
          </Button>
        </div>
      </div>
    )
  }

  renderOldVersionTodo = () => {
    const { contractUrlData } = this.props
    const contractUrl = contractUrlData?.url
    const contractFeedback = contractUrlData?.feedback

    return (
      <div className="code-conduct-modal-old-version">
        <div className="old-version-content">
          <div className="old-version-header">
            <h3>Código de Ética</h3>
          </div>
          
          <div className="old-version-message">
            <p className="main-message">
              Para continuar operando en la plataforma, te pedimos firmes el código de ética.
            </p>
            
            {contractFeedback?.loading ? (
              <div className="loading-message">
                <Spinner size={20} />
                <span>Cargando información del contrato...</span>
              </div>
            ) : contractFeedback?.error ? (
              <div className="error-message">
                <IonIcon icon="ios-warning" />
                <span>{contractFeedback.errors?.errors || 'Pregunta a tu equipo de soporte por él.'}</span>
              </div>
            ) : contractUrl ? (
              <div className="contract-action">
                <div className="sign-button-container">
                  <Button
                    type="button"
                    color="primary"
                    size="large"
                    onClick={() => window.open(contractUrl, '_blank')}
                  >
                    <IonIcon icon="ios-create" style={{ marginRight: '8px' }} />
                    Firmar Código de Ética
                  </Button>
                </div>
                <div className="refresh-button-container">
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={() => window.location.reload()}
                  >
                    <IonIcon icon="ios-refresh" style={{ marginRight: '8px' }} />
                    Ya firmé, recargar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="fallback-message">
                <IonIcon icon="ios-help-circle" />
                <span>Pregunta a tu equipo de soporte por él.</span>
              </div>
            )}
          </div>
          
          <div className="old-version-note">
            <IonIcon icon="ios-information-circle" />
            <span>
              <strong>IMPORTANTE:</strong> Es necesario firmar este documento para poder continuar operando en la plataforma.
            </span>
          </div>
        </div>
        
        <style jsx>{oldVersionCodeConductModalStyles}</style>
      </div>
    )
  }

  renderContractViewer = () => {
    const { documentUrl, isAccepting, codeConductFeedback } = this.props
    const { hasScrollCompleted } = this.state
    const hasAcceptanceError = codeConductFeedback?.acceptanceError
    const isProcessingAcceptance = codeConductFeedback?.acceptanceLoading

    if (isProcessingAcceptance) {
      return (
        <div className="code-conduct-modal-accepting">
          <div className="accepting-content">
            <Spinner size={60} />
            <h3>Procesando aceptación...</h3>
            <p>
              Estamos registrando la aceptación de su código de conducta. 
              Por favor, espere un momento.
            </p>
          </div>
        </div>
      )
    }

    if (hasAcceptanceError) {
      return (
        <div className="code-conduct-modal-acceptance-error">
          <div className="error-content">
            <IonIcon icon="ios-warning" />
            <h3>Error al procesar aceptación</h3>
            <p>
              Hubo un error al registrar la aceptación del código de conducta.
              Por favor, inténtelo nuevamente.
            </p>
          </div>
          <div className="error-actions">
            <Button
              type="button"
              color="primary"
              outline
              onClick={this.handleAcceptContract}
            >
              Reintentar
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="code-conduct-contract-viewer">
        <div className="contract-header">
          <h3>Código de Ética</h3>
          <p>
            Para continuar operando en la plataforma, te pedimos aceptes sus términos y condiciones
          </p>
        </div>

        <div className="contract-document">
          <PDFViewer
            file={documentUrl}
            documentName="Código de Ética"
            onCompleteScroll={this.handleScrollComplete}
          >
            <div className="contract-actions">
              <Button
                type="button"
                color="primary"
                size="large"
                fullWidth
                disabled={isAccepting || !hasScrollCompleted}
                loading={isAccepting}
                onClick={this.handleAcceptContract}
              >
                {isAccepting ? (
                  <>
                    <Spinner size={20} />
                    Procesando...
                  </>
                ) : (
                  <>
                    Acepto el Código de Ética
                    <IonIcon style={{ marginLeft: '8px' }} icon="ios-checkmark" />
                  </>
                )}
              </Button>
            </div>
          </PDFViewer>
        </div>
        <style jsx>{codeConductModalStyles}</style>
      </div>
    )
  }

  render() {
    const { isOpen, onClose, codeConductFeedback, consentType, documentUrl } = this.props
    
    const isLoading = codeConductFeedback?.loading
    const hasError = codeConductFeedback?.error
    const isOldVersion = consentType === 'SIGNATURE'
    const isNewVersion = consentType === 'ACCEPTANCE'
    const hasDocumentUrl = documentUrl && documentUrl.length > 0

    return (
      <>
        <div className={`modal-overlay ${isOpen ? 'visible' : ''}`} />

        <Modal
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton={false}
          closeOnOutClick={false}
          width="95%"
          maxWidth="1100px"
          className="code-conduct-modal"
        >
          <div className="code-conduct-modal-container">
            {isLoading && this.renderLoadingState()}
            
            {hasError && this.renderErrorState()}
            
            {isOldVersion && this.renderOldVersionTodo()}
            
            {isNewVersion && hasDocumentUrl && this.renderContractViewer()}
            
            {isNewVersion && !hasDocumentUrl && !isLoading && !hasError && (
              <div className="code-conduct-modal-no-document">
                <div className="no-document-content">
                  <IonIcon icon="ios-document" />
                  <h3>Documento no disponible</h3>
                  <p>No se pudo obtener el documento del código de conducta.</p>
                </div>
                <div className="no-document-actions">
                  <Button
                    type="button"
                    color="primary"
                    outline
                    onClick={() => this.props.checkConsentType()}
                  >
                    Reintentar
                  </Button>
                </div>
              </div>
            )}
          </div>
          <style jsx>{codeConductModalStyles}</style>
        </Modal>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  codeConductFeedback: state.codeConduct.feedback,
  consentType: state.codeConduct.consentType,
  documentUrl: state.codeConduct.documentUrl,
  isAccepting: state.codeConduct.acceptanceLoading,
  contractUrlData: urlForContractSelector(state),
})

const mapDispatchToProps = {
  checkConsentType: CodeConductActionCreators.checkConsentType,
  acceptCodeConductContract: CodeConductActionCreators.acceptCodeConductContract,
  getUrlByContract: ContractVersionActionCreators.getUrlByContract,
}

export default connect(mapStateToProps, mapDispatchToProps)(CodeConductModal)
